import ApiInstance from './instance'
import {
  EUserStatus,
  EViewedUpdate,
  IFilterUserDTO,
  IManagementUsersFiltersDTO,
  IResetPasswordDTO,
  ISignInRes,
  ISetupWizardUser,
  IUser,
  IUserDTO,
  IUserResponseDTO,
  URL_MAPPING,
  UserReassignment
} from '@zunta/configurations'

const { user: USER_MAPPING } = URL_MAPPING

export default class UserApi {
  instance: ApiInstance

  constructor (instance: ApiInstance) {
    this.instance = instance
  }

  createUser (data: Partial<IUser>) {
    return this.instance.doPost(`${USER_MAPPING.prefix}${USER_MAPPING.addUser.path}`, data)
  }

  getAdminUsersList (query: IFilterUserDTO): Promise<IUserResponseDTO> {
    return this.instance.doGet(`${USER_MAPPING.prefix}${USER_MAPPING.adminUsers.path}`, query)
  }

  updateUser (id: number, data: IUserDTO) {
    return this.instance.doPut(`${USER_MAPPING.prefix}${USER_MAPPING.updateUser.path}/${id}`, data)
  }

  checkUserAssignments (userId: number, facilities?: number[]) {
    return this.instance.doGet(`${USER_MAPPING.prefix}/checkUserAssignments/${userId}`, { facilities })
  }

  makeUserReassignments (userReassignments: UserReassignment[]) {
    return this.instance.doPut(`${USER_MAPPING.prefix}/makeUserReassignments`, userReassignments)
  }

  updateStatus (id: number, status: EUserStatus) {
    return this.instance.doPut(`${USER_MAPPING.prefix}${USER_MAPPING.updateUserStatus.path}/${id}/status`, { status })
  }

  getUserById (id: number | string) {
    return this.instance.doGet(`${USER_MAPPING.prefix}${USER_MAPPING.getUserById.path}/${id}`, {}, 'getUserById')
  }
  getChainManager (chainId: number | string) {
    return this.instance.doGet(`${USER_MAPPING.prefix}${USER_MAPPING.getChainManager.path}/${chainId}`, {}, 'getChainManager')
  }

  getManagementUsersList (query: IManagementUsersFiltersDTO) {
    return this.instance.doGet(`${USER_MAPPING.prefix}${USER_MAPPING.managementUsers.path}`, query)
  }

  resetPassword (dto: IResetPasswordDTO): Promise<ISignInRes & { statusCode: number, message: string }> {
    return this.instance.doPost(`${USER_MAPPING.prefix}${USER_MAPPING.resetPassword.path}`, dto)
  }

  getPagination (filters: any): Promise<any> {
    return this.instance.doGet(`${USER_MAPPING.prefix}/pagination`, filters)
  }

  changeUserAvatar (userId: number | string, file: any) {
    const formData = new FormData()
    formData.append('avatar', file)
    return this.instance.doPatch(`${USER_MAPPING.prefix}/uploadAvatar/${userId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  importChainAdminUsers (file: File) {
    const formData = new FormData()
    formData.append('report', file)
    return this.instance.doPost(`${USER_MAPPING.prefix}/importChainAdminUsers`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  addChainUserComment (userId: number | string, data: any) {
    return this.instance.doPost(`${USER_MAPPING.prefix}/chainUserComments/${userId}/add`, data)
  }
  removeChainUserComment (commentId: number | string) {
    return this.instance.doDelete(`${USER_MAPPING.prefix}/chainUserComments/${commentId}/remove`)
  }
  updateChainUserComment (commentId: number | string, text: string) {
    return this.instance.doPatch(`${USER_MAPPING.prefix}/chainUserComments/${commentId}/update`, { text })
  }
  fetchChainUserComments (userId: number | string) {
    return this.instance.doGet(`${USER_MAPPING.prefix}/chainUserComments/${userId}/getAll`)
  }
  changeChainUserCommentPin (commentId: number, pin: boolean) {
    return this.instance.doPatch(`${USER_MAPPING.prefix}/chainUserComments/${commentId}/change-pin`, { pin })
  }
  getFacilityUsers (facilityId: number): Promise<IUser[]> {
    return this.instance.doGet(`${USER_MAPPING.prefix}${USER_MAPPING.getFacilityUsers.path}/${facilityId}`, {}, 'getFacilityUsers')
  }
  setupWizardUsers (data: ISetupWizardUser[]): Promise<'OK'> {
    return this.instance.doPost(`${USER_MAPPING.prefix}${USER_MAPPING.setupChainUsers.path}`, { users: data })
  }
  getIsEmailsAvailable (emails: string[]): Promise<Record<string, boolean>> {
    return this.instance.doGet(`${USER_MAPPING.prefix}/getIsEmailsAvailable/`, { emails })
  }

  setViewedVideoModule (viewedUpdate: EViewedUpdate) {
    return this.instance.doPost(`${USER_MAPPING.prefix}/viewedUpdate`, { viewedUpdate })
  }

  getViewedVideoModule (userId: string | number) {
    return this.instance.doGet(`${USER_MAPPING.prefix}/viewedUpdates/${userId}`)
  }

  getQaReportsSettings (userId: string | number) {
    return this.instance.doGet(`${USER_MAPPING.prefix}/qaReports/${userId}`)
  }

  saveQaReportsSettings (userId: string | number, reports: string[]) {
    return this.instance.doPost(`${USER_MAPPING.prefix}/qaReports/${userId}`, reports)
  }
}
